import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { sessionStorage } from 'app/services/storage';
import EnrollPasskeyModal from 'app/shared/components/EnrollPasskeyModal';

import { PASSKEY_ENROLLMENT_PROMPT_FLAG } from 'constants/auth';
import { isWebAuthnSupported } from 'utils/webAuthn';

import { getMasterRole } from 'store/auth/selectors';

/**
 * DEV-3881 enroll-on-first-login: when a PasskeyWithUsername user signed in via the OTP/password fallback
 * this session (flag set in Login, or on the TPO auto-login handoff), prompt them ONCE to enroll a passkey
 * on this device — right after sign-in.
 *
 * DEV-4083: consume the flag on the AUTHENTICATED transition (master role populated), NOT on raw mount.
 * This host lives in the always-on DefaultLayout, which first mounts on the /login route — before the user
 * arms the flag — so a mount-only read saw an empty flag, never prompted after sign-in, and left the flag
 * lingering in sessionStorage to resurface on the next full page reload, on whatever screen the user was on.
 * Keying the effect on the master role re-runs it the moment sign-in populates auth state, on the post-login
 * screen. The flag is consumed even when WebAuthn is unsupported, so it can never linger to a later reload.
 */
const PasskeyEnrollmentHost = () => {
  const [isOpen, setIsOpen] = useState(false);
  const masterRole = useSelector(getMasterRole);
  const hasPromptedRef = useRef(false);

  useEffect(() => {
    if (hasPromptedRef.current || !masterRole) {
      return;
    }

    hasPromptedRef.current = true;

    const shouldPrompt = sessionStorage.getItem(PASSKEY_ENROLLMENT_PROMPT_FLAG);
    sessionStorage.removeItem(PASSKEY_ENROLLMENT_PROMPT_FLAG);

    if (shouldPrompt && isWebAuthnSupported()) {
      setIsOpen(true);
    }
  }, [masterRole]);

  const onClose = useCallback(() => setIsOpen(false), []);

  return <EnrollPasskeyModal isOpen={isOpen} onClose={onClose} />;
};

export default PasskeyEnrollmentHost;
